import * as THREE from 'three';
import { Chunk } from './chunk';
import { getBlock, isTransparent, isCutout } from './block-registry';
import { BlockId } from '@shared/block-ids';
import { CHUNK_WIDTH, CHUNK_DEPTH, CHUNK_HEIGHT } from '@shared/chunk-constants';
import { getUV } from '../utils/texture-atlas';
import { Face } from '../utils/direction';

interface ChunkNeighbors {
  px?: Chunk; // +X
  nx?: Chunk; // -X
  pz?: Chunk; // +Z
  nz?: Chunk; // -Z
}

interface MeshData {
  positions: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
  colors: number[];
}

function getNeighborBlock(
  chunk: Chunk,
  neighbors: ChunkNeighbors,
  x: number, y: number, z: number,
): BlockId {
  if (y < 0 || y >= CHUNK_HEIGHT) return BlockId.AIR;

  if (x < 0) {
    return neighbors.nx ? neighbors.nx.getBlock(CHUNK_WIDTH - 1, y, z) : BlockId.AIR;
  }
  if (x >= CHUNK_WIDTH) {
    return neighbors.px ? neighbors.px.getBlock(0, y, z) : BlockId.AIR;
  }
  if (z < 0) {
    return neighbors.nz ? neighbors.nz.getBlock(x, y, CHUNK_DEPTH - 1) : BlockId.AIR;
  }
  if (z >= CHUNK_DEPTH) {
    return neighbors.pz ? neighbors.pz.getBlock(x, y, 0) : BlockId.AIR;
  }

  return chunk.getBlock(x, y, z);
}

function computeAO(
  chunk: Chunk,
  neighbors: ChunkNeighbors,
  x: number, y: number, z: number,
  d1x: number, d1y: number, d1z: number,
  d2x: number, d2y: number, d2z: number,
): number {
  const side1 = !isTransparent(getNeighborBlock(chunk, neighbors, x + d1x, y + d1y, z + d1z));
  const side2 = !isTransparent(getNeighborBlock(chunk, neighbors, x + d2x, y + d2y, z + d2z));
  const corner = !isTransparent(getNeighborBlock(chunk, neighbors, x + d1x + d2x, y + d1y + d2y, z + d1z + d2z));

  if (side1 && side2) return 0;
  return 3 - (side1 ? 1 : 0) - (side2 ? 1 : 0) - (corner ? 1 : 0);
}

function addFace(
  mesh: MeshData,
  face: Face,
  x: number, y: number, z: number,
  blockId: BlockId,
  chunk: Chunk,
  neighbors: ChunkNeighbors,
): void {
  const block = getBlock(blockId);
  const idx = mesh.positions.length / 3;

  let texIdx: number;
  switch (face) {
    case Face.TOP: texIdx = block.textures.top; break;
    case Face.BOTTOM: texIdx = block.textures.bottom; break;
    case Face.FRONT: texIdx = block.textures.front; break;
    case Face.BACK: texIdx = block.textures.back; break;
    case Face.RIGHT: texIdx = block.textures.right; break;
    case Face.LEFT: texIdx = block.textures.left; break;
  }

  const [u0, v0, u1, v1] = getUV(texIdx);

  let positions: number[];
  let normal: [number, number, number];
  let ao: number[];

  switch (face) {
    case Face.TOP:
      positions = [x, y + 1, z, x + 1, y + 1, z, x + 1, y + 1, z + 1, x, y + 1, z + 1];
      normal = [0, 1, 0];
      ao = [
        computeAO(chunk, neighbors, x, y + 1, z, -1, 0, 0, 0, 0, -1),
        computeAO(chunk, neighbors, x + 1, y + 1, z, 1, 0, 0, 0, 0, -1),
        computeAO(chunk, neighbors, x + 1, y + 1, z + 1, 1, 0, 0, 0, 0, 1),
        computeAO(chunk, neighbors, x, y + 1, z + 1, -1, 0, 0, 0, 0, 1),
      ];
      break;
    case Face.BOTTOM:
      positions = [x, y, z + 1, x + 1, y, z + 1, x + 1, y, z, x, y, z];
      normal = [0, -1, 0];
      ao = [
        computeAO(chunk, neighbors, x, y - 1, z + 1, -1, 0, 0, 0, 0, 1),
        computeAO(chunk, neighbors, x + 1, y - 1, z + 1, 1, 0, 0, 0, 0, 1),
        computeAO(chunk, neighbors, x + 1, y - 1, z, 1, 0, 0, 0, 0, -1),
        computeAO(chunk, neighbors, x, y - 1, z, -1, 0, 0, 0, 0, -1),
      ];
      break;
    case Face.FRONT:
      positions = [x, y, z + 1, x + 1, y, z + 1, x + 1, y + 1, z + 1, x, y + 1, z + 1];
      normal = [0, 0, 1];
      ao = [
        computeAO(chunk, neighbors, x, y, z + 1, -1, 0, 0, 0, -1, 0),
        computeAO(chunk, neighbors, x + 1, y, z + 1, 1, 0, 0, 0, -1, 0),
        computeAO(chunk, neighbors, x + 1, y + 1, z + 1, 1, 0, 0, 0, 1, 0),
        computeAO(chunk, neighbors, x, y + 1, z + 1, -1, 0, 0, 0, 1, 0),
      ];
      break;
    case Face.BACK:
      positions = [x + 1, y, z, x, y, z, x, y + 1, z, x + 1, y + 1, z];
      normal = [0, 0, -1];
      ao = [
        computeAO(chunk, neighbors, x + 1, y, z - 1, 1, 0, 0, 0, -1, 0),
        computeAO(chunk, neighbors, x, y, z - 1, -1, 0, 0, 0, -1, 0),
        computeAO(chunk, neighbors, x, y + 1, z - 1, -1, 0, 0, 0, 1, 0),
        computeAO(chunk, neighbors, x + 1, y + 1, z - 1, 1, 0, 0, 0, 1, 0),
      ];
      break;
    case Face.RIGHT:
      positions = [x + 1, y, z + 1, x + 1, y, z, x + 1, y + 1, z, x + 1, y + 1, z + 1];
      normal = [1, 0, 0];
      ao = [
        computeAO(chunk, neighbors, x + 1, y, z + 1, 0, 0, 1, 0, -1, 0),
        computeAO(chunk, neighbors, x + 1, y, z, 0, 0, -1, 0, -1, 0),
        computeAO(chunk, neighbors, x + 1, y + 1, z, 0, 0, -1, 0, 1, 0),
        computeAO(chunk, neighbors, x + 1, y + 1, z + 1, 0, 0, 1, 0, 1, 0),
      ];
      break;
    case Face.LEFT:
      positions = [x, y, z, x, y, z + 1, x, y + 1, z + 1, x, y + 1, z];
      normal = [-1, 0, 0];
      ao = [
        computeAO(chunk, neighbors, x - 1, y, z, 0, 0, -1, 0, -1, 0),
        computeAO(chunk, neighbors, x - 1, y, z + 1, 0, 0, 1, 0, -1, 0),
        computeAO(chunk, neighbors, x - 1, y + 1, z + 1, 0, 0, 1, 0, 1, 0),
        computeAO(chunk, neighbors, x - 1, y + 1, z, 0, 0, -1, 0, 1, 0),
      ];
      break;
  }

  mesh.positions.push(...positions);

  for (let i = 0; i < 4; i++) {
    mesh.normals.push(...normal);
  }

  mesh.uvs.push(u0, v1, u1, v1, u1, v0, u0, v0);

  // AO as vertex color brightness
  for (const a of ao) {
    const brightness = (a / 3) * 0.4 + 0.6;
    mesh.colors.push(brightness, brightness, brightness);
  }

  // Flip quad if AO requires it for correct interpolation
  if (ao[0] + ao[2] > ao[1] + ao[3]) {
    mesh.indices.push(idx, idx + 1, idx + 2, idx, idx + 2, idx + 3);
  } else {
    mesh.indices.push(idx, idx + 1, idx + 3, idx + 1, idx + 2, idx + 3);
  }
}

export function buildChunkMesh(
  chunk: Chunk,
  neighbors: ChunkNeighbors,
): { solid: THREE.BufferGeometry | null; cutout: THREE.BufferGeometry | null; transparent: THREE.BufferGeometry | null } {
  const solidMesh: MeshData = { positions: [], normals: [], uvs: [], indices: [], colors: [] };
  const cutoutMesh: MeshData = { positions: [], normals: [], uvs: [], indices: [], colors: [] };
  const transparentMesh: MeshData = { positions: [], normals: [], uvs: [], indices: [], colors: [] };

  for (let y = 0; y < CHUNK_HEIGHT; y++) {
    for (let z = 0; z < CHUNK_DEPTH; z++) {
      for (let x = 0; x < CHUNK_WIDTH; x++) {
        const blockId = chunk.getBlock(x, y, z);
        if (blockId === BlockId.AIR) continue;

        const blockDef = getBlock(blockId);
        // Route to correct mesh: cutout (leaves/flowers) > transparent (glass/water) > solid
        const mesh = blockDef.cutout ? cutoutMesh : blockDef.transparent ? transparentMesh : solidMesh;

        // Check each face
        const above = getNeighborBlock(chunk, neighbors, x, y + 1, z);
        const below = getNeighborBlock(chunk, neighbors, x, y - 1, z);
        const front = getNeighborBlock(chunk, neighbors, x, y, z + 1);
        const back = getNeighborBlock(chunk, neighbors, x, y, z - 1);
        const right = getNeighborBlock(chunk, neighbors, x + 1, y, z);
        const left = getNeighborBlock(chunk, neighbors, x - 1, y, z);

        if (isTransparent(above) && above !== blockId)
          addFace(mesh, Face.TOP, x, y, z, blockId, chunk, neighbors);
        if (isTransparent(below) && below !== blockId)
          addFace(mesh, Face.BOTTOM, x, y, z, blockId, chunk, neighbors);
        if (isTransparent(front) && front !== blockId)
          addFace(mesh, Face.FRONT, x, y, z, blockId, chunk, neighbors);
        if (isTransparent(back) && back !== blockId)
          addFace(mesh, Face.BACK, x, y, z, blockId, chunk, neighbors);
        if (isTransparent(right) && right !== blockId)
          addFace(mesh, Face.RIGHT, x, y, z, blockId, chunk, neighbors);
        if (isTransparent(left) && left !== blockId)
          addFace(mesh, Face.LEFT, x, y, z, blockId, chunk, neighbors);
      }
    }
  }

  return {
    solid: meshDataToGeometry(solidMesh),
    cutout: meshDataToGeometry(cutoutMesh),
    transparent: meshDataToGeometry(transparentMesh),
  };
}

function meshDataToGeometry(data: MeshData): THREE.BufferGeometry | null {
  if (data.positions.length === 0) return null;

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(data.positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(data.normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(data.uvs, 2));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(data.colors, 3));
  geo.setIndex(data.indices);
  geo.computeBoundingSphere();
  return geo;
}
